import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { View } from "react-native";
import styled, { useTheme } from "styled-components/native";
import { CheckBox, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { SelectionType } from "@components/atoms/Filters/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useDispatch, useSelector } from "react-redux";
import {
  setSubCollection,
  setResetInProgress,
  resetFiltersPayloadByProduct,
  applyCollectionSubcategorySelection,
  setSectionName,
} from "@redux/slices/searchFilters";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";

import LineViewContainer from "@scenes/requests/utils/LineView";
import { CollectionSubcategoriesProps } from "./types";
import RadioButton from "../RadioButton";

const CollectionSubcategories = ({
  options = [],
  selectionType = SelectionType.multiple,
  sectionTitle = "",

  isRoundCheckbox,
  sectionName = "",
  direction = "column",

  hasSeparator = false,

  isCollapsible,
  collectionDependencies = [],
  selectedProduct,
}: CollectionSubcategoriesProps) => {
  if (!options) {
    return null;
  }

  // Must use the live collection array — equality-by-visibility kept a stale
  // list after reset, and the sync effect re-applied the old subcategory.
  const collections = useSelector(
    (state: any) => state.searchFilters.collection
  );
  const isResetInProgress = useSelector(
    (state: any) => state.searchFilters.isResetInProgress
  );
  const typography = useTypographyStore();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isTouched = useRef(false);
  const registeredRef = useRef<string>("");
  const selectedCollectionsRef = useRef<string[]>([]);

  const [selectedCollections, setSelectedCollections] = useState<string[]>(
    () => {
      if (!collections || !options) return [];

      const relevantCollections = options
        .map((option) => option.collection)
        .filter((collection) => collections.includes(collection));

      relevantCollections.forEach((collection) => {
        dispatch(setSubCollection(collection));
      });

      return relevantCollections;
    }
  );
  selectedCollectionsRef.current = selectedCollections;

  useEffect(() => {
    if (
      collections &&
      options &&
      selectedCollections.length > 0 &&
      !isTouched.current &&
      !isResetInProgress
    ) {
      const registrationKey = selectedCollections.join("|");
      if (registeredRef.current === registrationKey) return;
      registeredRef.current = registrationKey;

      selectedCollections.forEach((collection) => {
        dispatch(setSubCollection(collection));
      });
    }
  }, [collections, options, selectedCollections, isResetInProgress]);

  useEffect(() => {
    if (!collections || !options || isTouched.current) return;

    const collectionsSet = new Set(collections);
    const relevantCollections = options
      .map((option) => option.collection)
      .filter((collection) => collectionsSet.has(collection));

    const selectedSetLocal = new Set(selectedCollections);
    const relevantSet = new Set(relevantCollections);
    const isSame =
      relevantCollections.length === selectedCollections.length &&
      relevantCollections.every((col) => selectedSetLocal.has(col)) &&
      selectedCollections.every((col) => relevantSet.has(col));

    if (!isSame) {
      setSelectedCollections(relevantCollections);

      relevantCollections.forEach((collection) => {
        dispatch(setSubCollection(collection));
      });
    }
  }, [collections, options, selectedCollections]);

  const isVisible = useMemo(() => {
    if (!collectionDependencies || collectionDependencies.length === 0) {
      return true;
    }
    if (!collections || collections.length === 0) {
      return false;
    }

    return collectionDependencies.some((dep) => collections.includes(dep));
  }, [collectionDependencies, collections]);

  useEffect(() => {
    if (!isVisible && selectedCollections.length > 0) {
      setSelectedCollections([]);
    }
  }, [isVisible]);

  useEffect(() => {
    if (isResetInProgress) {
      if (selectedCollections.length > 0) {
        setSelectedCollections([]);
      }
      isTouched.current = false;
      registeredRef.current = "";
      setTimeout(() => {
        dispatch(setResetInProgress(false));
      }, 0);
    }
  }, [isResetInProgress]);

  const handleItemSelection = useCallback(
    (item: any) => {
      isTouched.current = true;
      if (isResetInProgress) {
        dispatch(setResetInProgress(false));
      }
      const itemCollection = item.collection;

      if (selectionType === SelectionType.single) {
        const isDeselecting =
          selectedCollectionsRef.current.includes(itemCollection);
        // Only wipe other filters when switching to a different subcategory.
        // Unselecting must not clear section-level lab_grown / sub_type.
        if (!isDeselecting && selectedCollectionsRef.current.length > 0) {
          dispatch(resetFiltersPayloadByProduct(selectedProduct));
        }
        setSelectedCollections((prev) =>
          prev.includes(itemCollection) ? [] : [itemCollection]
        );
      } else {
        setSelectedCollections((prev) =>
          prev.includes(itemCollection)
            ? prev.filter((collection) => collection !== itemCollection)
            : [...prev, itemCollection]
        );
      }
    },
    [selectionType, isResetInProgress, dispatch, selectedProduct]
  );

  const selectedSet = useMemo(
    () => new Set(selectedCollections),
    [selectedCollections]
  );

  useEffect(() => {
    if (!isTouched.current) return;

    const selectedLocal = new Set(selectedCollections);
    const sectionCollections = options.map((option) => option.collection);

    const payload: any = {};
    options.forEach((item) => {
      if (!selectedLocal.has(item.collection) || !item.predefined_payload) {
        return;
      }
      Object.entries(item.predefined_payload).forEach(([key, value]) => {
        if (!payload[key]) {
          payload[key] = value;
        } else if (Array.isArray(payload[key]) && Array.isArray(value)) {
          const merged = [...payload[key]];
          (value as any[]).forEach((v) => {
            if (!merged.includes(v)) {
              merged.push(v);
            }
          });
          payload[key] = merged;
        } else {
          payload[key] = value;
        }
      });
    });

    let removePayloadKeys: string[] = [];
    if (selectedCollections.length === 0) {
      const keys = new Set<string>();
      options.forEach((option) => {
        if (option.predefined_payload) {
          Object.keys(option.predefined_payload).forEach((key) => {
            if (key !== "sub_type" && key !== "lab_grown") {
              keys.add(key);
            }
          });
        }
      });
      removePayloadKeys = Array.from(keys);
    }

    dispatch(
      applyCollectionSubcategorySelection({
        sectionCollections,
        selectedCollections,
        sectionName,
        payload,
        removePayloadKeys,
      })
    );

    if (
      options.some(
        (item) =>
          selectedLocal.has(item.collection) &&
          item.predefined_payload?.sub_type === "labgrown"
      )
    ) {
      dispatch(setSectionName(SearchFilterTypeEnum.LAB_GROWN_JEWELRY));
    }
  }, [selectedCollections, options, sectionName, dispatch]);

  if (!isVisible) {
    return null;
  }

  return (
    <TopLevelView>
      {sectionTitle && (
        <>
          {!isCollapsible && (
            <ParentContainer>
              <VDBText
                isBold
                type={typography["H3-M"]}
                color={theme["core-05"]}
              >
                {sectionTitle}
              </VDBText>
            </ParentContainer>
          )}
          {hasSeparator && (
            <>
              <LineViewContainer background="core-03" />
              <Spacer size={SpaceType.Extra} />
            </>
          )}
        </>
      )}
      <CheckBoxParentContainer direction={direction}>
        {options.map((item, index) => (
          <OptionRow
            key={item.collection ?? index}
            item={item}
            index={index}
            isLast={options.length - 1 === index}
            checked={selectedSet.has(item.collection)}
            direction={direction}
            isRoundCheckbox={isRoundCheckbox}
            hasSeparator={hasSeparator}
            onPress={handleItemSelection}
            typography={typography}
            theme={theme}
          />
        ))}
      </CheckBoxParentContainer>
      {sectionTitle && <Spacer size={SpaceType.ExtraSmall} />}
    </TopLevelView>
  );
};

const CheckBoxDisplay = styled.View`
  flex-direction: row;
  align-items: center;
  padding-vertical: 2px;
`;

const CheckBoxParentContainer = styled.View<{
  direction: string;
}>`
  flex-direction: ${({ direction }: { direction: string }) => direction};
  flex-wrap: ${({ direction }: { direction: string }) =>
    direction === "column" ? "wrap" : "no-wrap"};
  width: 100%;
  flex-grow: 1;
`;

const CheckboxChildView = styled.Pressable<{ direction: string }>`
  width: ${({ direction }: { direction: string }) =>
    direction === "column" ? "100%" : "auto"};
  ${({ direction }: { direction: string }) =>
    direction === "column" ? "flex-grow: 1;" : ""};
`;

const CheckBoxIconView = styled.View<{
  direction: string;
}>`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
  width: ${({ direction }: { direction: string }) =>
    direction === "column" ? "100%" : "auto"};
  padding-top: ${({ index }: { index: number }) =>
    index === 0 ? `0px` : `${SpaceType.Extra}px`};
  padding-bottom: ${({ lastitem }: { lastitem: boolean }) =>
    lastitem ? `${SpaceType.Basic}px` : `${SpaceType.Extra}px`};
`;

const ParentContainer = styled.View`
  padding: 14.5px ${SpaceType.Double}px;
`;

const TopLevelView = styled.View``;

type OptionRowProps = {
  item: any;
  index: number;
  isLast: boolean;
  checked: boolean;
  direction: string;
  isRoundCheckbox?: boolean;
  hasSeparator: boolean;
  onPress: (item: any) => void;
  typography: any;
  theme: any;
};

const OptionRow = memo(
  ({
    item,
    index,
    isLast,
    checked,
    direction,
    isRoundCheckbox,
    hasSeparator,
    onPress,
    typography,
    theme,
  }: OptionRowProps) => {
    const handlePress = useCallback(() => onPress(item), [onPress, item]);

    return (
      <CheckboxChildView direction={direction} onPress={handlePress}>
        <CheckBoxIconView index={index} lastitem={isLast} direction={direction}>
          <CheckBoxDisplay>
            {/* Row is the only Pressable — nested CheckBox/Radio onPress
                fired twice and cancelled the 2nd selection. */}
            <View pointerEvents="none">
              {isRoundCheckbox ? (
                <RadioButton checked={checked} onPress={() => {}} margin="0" />
              ) : (
                <CheckBox
                  // @ts-ignore
                  accessibilityValue={item.name}
                  isChecked={checked}
                  checkBoxSize={20}
                />
              )}
            </View>

            <VDBText
              type={checked ? typography["Minor-M"] : typography["Minor-R"]}
              color={checked ? theme["core-05"] : theme["core-06"]}
            >
              {item.label}
            </VDBText>
          </CheckBoxDisplay>
          {item.icon?.data && item.icon?.type === "font" && (
            <VDBIcon
              variant={IconsVariant.VDBCustom}
              name={item.icon.data}
              size={24}
            />
          )}
        </CheckBoxIconView>
        {hasSeparator && !isLast && <LineViewContainer background="core-03" />}
      </CheckboxChildView>
    );
  }
);

OptionRow.displayName = "OptionRow";

export default memo(CollectionSubcategories);
